import type { GetImpulseFormInput, ImpulseFormShape } from "~/tools/impulse-form";

import type { WidgetCalculation } from "./calculation/form-state";
import type { WidgetConnectNulls } from "./connect-nulls/form-state";
import type { WidgetDecimals } from "./decimals/form-state";
import type { WidgetFontSize } from "./font-size/form-state";
import type { WidgetLegendMode, WidgetLegendPosition, WidgetLegendTable } from "./legend/form-state";
import type { WidgetMetricLabel } from "./metric-label/form-state";
import type { WidgetRange } from "./range/form-state";
import type { WidgetSparkline } from "./sparkline/form-state";
import type { WidgetThresholdsColor, WidgetThresholdsMode, WidgetThresholdsStep } from "./thresholds/form-state";
import type { WidgetUnit } from "./unit/form-state";
import type { WidgetYAxisLabel } from "./y-axis/form-state";
import { BarChartFormState } from "./bar-chart-form-state";
import { GaugeChartFormState } from "./gauge-chart-form-state";
import { StatChartFormState } from "./stat-chart-form-state";
import { TimeSeriesChartFormState } from "./time-series-chart-form-state";

interface WidgetFormSync {
	unit: GetImpulseFormInput<WidgetUnit>;
	decimals: GetImpulseFormInput<WidgetDecimals>;
	connectNulls?: GetImpulseFormInput<WidgetConnectNulls>;
	min?: GetImpulseFormInput<WidgetRange>;
	max?: GetImpulseFormInput<WidgetRange>;
	metricLabel?: GetImpulseFormInput<WidgetMetricLabel>;
	fontSize?: GetImpulseFormInput<WidgetFontSize>;
	sparkline?: GetImpulseFormInput<WidgetSparkline>;
	calculation?: GetImpulseFormInput<WidgetCalculation>;
	color?: GetImpulseFormInput<WidgetThresholdsColor>;
	legend?: {
		enabled?: boolean;
		position?: GetImpulseFormInput<WidgetLegendPosition>;
		mode?: GetImpulseFormInput<WidgetLegendMode>;
		table?: GetImpulseFormInput<WidgetLegendTable>;
	};
	yAxis?: {
		enabled?: boolean;
		label?: GetImpulseFormInput<WidgetYAxisLabel>;
	};
	thresholds: {
		mode?: GetImpulseFormInput<WidgetThresholdsMode>;
		steps: ReadonlyArray<GetImpulseFormInput<WidgetThresholdsStep>>;
	};
}

interface WidgetsCollection {
	TimeSeriesChart: TimeSeriesChartFormState;
	BarChart: BarChartFormState;
	StatChart: StatChartFormState;
	GaugeChart: GaugeChartFormState;
}

function toSync(
	active: keyof WidgetsCollection,
	branches: Partial<GetImpulseFormInput<ImpulseFormShape<WidgetsCollection>>>,
): undefined | WidgetFormSync {
	switch (active) {
		case "TimeSeriesChart": {
			return branches.TimeSeriesChart == null ? undefined : TimeSeriesChartFormState.toSync(branches.TimeSeriesChart);
		}

		case "BarChart": {
			return branches.BarChart == null ? undefined : BarChartFormState.toSync(branches.BarChart);
		}

		case "GaugeChart": {
			return branches.GaugeChart == null ? undefined : GaugeChartFormState.toSync(branches.GaugeChart);
		}

		case "StatChart": {
			return branches.StatChart == null ? undefined : StatChartFormState.toSync(branches.StatChart);
		}
	}
}

function fromSync(sync: WidgetFormSync, collection: WidgetsCollection): void {
	TimeSeriesChartFormState.setInput(TimeSeriesChartFormState.fromSync(sync), collection.TimeSeriesChart);
	BarChartFormState.setInput(BarChartFormState.fromSync(sync), collection.BarChart);
	GaugeChartFormState.setInput(GaugeChartFormState.fromSync(sync), collection.GaugeChart);
	StatChartFormState.setInput(StatChartFormState.fromSync(sync), collection.StatChart);
}

export { fromSync, toSync };
export type { WidgetFormSync };
